"use client";

import * as React from "react";

interface CountUpProps {
	value: number;
	suffix?: string;
	duration?: number;
	className?: string;
	disabled?: boolean;
}

/**
 * Formats a number with dots as thousand separators (e.g., 7800 -> "7.800")
 */
const formatNumber = (num: number): string =>
	num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

export const CountUp: React.FC<CountUpProps> = ({
	value,
	suffix = "",
	duration = 2000,
	className,
	disabled = false,
}) => {
	const [count, setCount] = React.useState(0);
	const [hasStarted, setHasStarted] = React.useState(false);
	const ref = React.useRef<HTMLSpanElement>(null);

	React.useEffect(() => {
		if (hasStarted || !ref.current) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setHasStarted(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 },
		);
		observer.observe(ref.current);
		return () => observer.disconnect();
	}, [hasStarted]);

	React.useEffect(() => {
		if (!hasStarted) return;

		let frame: number;
		const startTime = performance.now();

		const animate = (now: number) => {
			const progress = Math.min((now - startTime) / duration, 1);
			const eased = 1 - Math.pow(1 - progress, 2);
			setCount(Math.round(value * eased));

			if (progress < 1) frame = requestAnimationFrame(animate);
		};

		frame = requestAnimationFrame(animate);
		return () => cancelAnimationFrame(frame);
	}, [hasStarted, value, duration]);

	if (disabled) {
		return (
			<span className={className}>
				{formatNumber(value)}
				{suffix && <span>{suffix}</span>}
			</span>
		);
	}

	return (
		<span ref={ref} className={className}>
			{formatNumber(count)}
			{suffix && <span>{suffix}</span>}
		</span>
	);
};
